import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, Navigate } from 'react-router-dom';
import { LogIn, ShieldAlert, Loader2 } from 'lucide-react';
import {
  startGoogleSignIn,
  processSessionId,
  fetchMe,
  BOTHY_PATH,
} from '@/lib/bothyAuth';
import BrandLogo from '@/components/BrandLogo';

/**
 * The Bothy — login screen.
 * Shown when a visitor hits /the-bothy without a valid session.
 */
export default function BothyLogin() {
  const navigate = useNavigate();
  const location = useLocation();
  const [checking, setChecking] = useState(true);
  // Derive the error message from router state synchronously — no effect needed.
  const error = location.state?.unauthorised
    ? 'Sorry — that Google account is not on the allowlist.'
    : null;

  // If we already have a session, send the user straight to the dashboard.
  useEffect(() => {
    let cancelled = false;
    // CRITICAL: skip the /me check when returning from OAuth — AuthCallback
    // (handled in App.js) will exchange the session_id first.
    if (window.location.hash?.includes('session_id=')) {
      setChecking(false);
      return;
    }
    fetchMe().then((u) => {
      if (cancelled) return;
      // Only redirect to the dashboard if this is actually an admin.
      // Non-admin customers who wander onto /the-bothy stay on the login
      // page with the "not on the allowlist" message.
      if (u && u.is_admin) navigate(`${BOTHY_PATH}/dashboard`, { replace: true });
      else if (u) setChecking(false);
      else setChecking(false);
    });
    return () => { cancelled = true; };
  }, [navigate]);

  if (checking) {
    return (
      <div className="min-h-screen grid place-items-center bg-[var(--brand-parchment)]">
        <Loader2 className="animate-spin text-[var(--brand-olive)]" size={28} />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-[var(--brand-forest-deep)] text-[var(--brand-parchment)] relative overflow-hidden">
      <div className="absolute inset-0 grain opacity-50 pointer-events-none" />
      <div className="relative z-10 max-w-md w-full px-6 py-12 text-center">
        <div className="flex justify-center mb-8">
          <BrandLogo size={120} />
        </div>
        <span className="font-eyebrow !text-[var(--brand-gold)]">The Bothy</span>
        <h1 className="mt-3 font-display text-5xl tracking-tight leading-[1.02]">
          A quiet workshop<br />
          <span className="italic text-[var(--brand-gold)]">for the founder.</span>
        </h1>
        <p className="mt-6 text-[var(--brand-parchment)]/80 leading-relaxed">
          Sign in with the authorised Google account to view enquiries,
          subscribers, your sales forecast, and the marketing tracker.
        </p>

        {error && (
          <div
            data-testid="bothy-login-error"
            className="mt-8 flex items-start gap-3 bg-red-900/40 border border-red-500/30 px-4 py-3 text-sm text-left"
          >
            <ShieldAlert size={18} className="text-red-300 mt-0.5 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        <button
          data-testid="bothy-google-signin"
          onClick={startGoogleSignIn}
          className="mt-10 btn-gold inline-flex items-center justify-center gap-2 px-7 py-3.5 text-sm font-semibold rounded-none"
        >
          <LogIn size={16} /> Sign in with Google
        </button>

        <p className="mt-10 text-xs uppercase tracking-[0.2em] opacity-60">
          Only allowlisted accounts may proceed.
        </p>
      </div>
    </div>
  );
}

/**
 * AuthCallback — handles `#session_id=...` returning from Emergent OAuth.
 * Mounted by AppRouter when the URL hash contains session_id.
 */
export function BothyAuthCallback() {
  const navigate = useNavigate();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash || '';
    const match = hash.match(/session_id=([^&]+)/);
    const sessionId = match?.[1];

    (async () => {
      if (!sessionId) {
        navigate(BOTHY_PATH, { replace: true });
        return;
      }
      try {
        const user = await processSessionId(sessionId);
        // Clean the URL fragment & route to the dashboard
        window.history.replaceState(null, '', BOTHY_PATH + '/dashboard');
        navigate(`${BOTHY_PATH}/dashboard`, { replace: true, state: { user } });
      } catch (err) {
        const detail = err?.response?.data?.detail;
        if (err?.response?.status === 403) {
          navigate(BOTHY_PATH, {
            replace: true,
            state: { unauthorised: true, detail },
          });
        } else {
          navigate(BOTHY_PATH, { replace: true });
        }
      }
    })();
  }, [navigate]);

  return (
    <div className="min-h-screen grid place-items-center bg-[var(--brand-forest-deep)] text-[var(--brand-parchment)]">
      <div className="flex items-center gap-3">
        <Loader2 className="animate-spin text-[var(--brand-gold)]" size={22} />
        <span>Opening the bothy…</span>
      </div>
    </div>
  );
}

/** Wrapper that gates a child page on a valid admin session. */
export function BothyProtected({ children }) {
  const location = useLocation();
  // If user data was just passed from AuthCallback, trust it for first render.
  const [state, setState] = useState(
    location.state?.user ? { ok: !!location.state.user.is_admin, user: location.state.user } : null,
  );

  useEffect(() => {
    if (state) return;
    fetchMe().then((u) => setState({ ok: !!u && !!u.is_admin, user: u }));
  }, [state]);

  if (state === null) {
    return (
      <div className="min-h-screen grid place-items-center bg-[var(--brand-parchment)]">
        <Loader2 className="animate-spin text-[var(--brand-olive)]" size={26} />
      </div>
    );
  }
  if (!state.ok) {
    // Signed-in customer (or anonymous) trying the admin door.
    return (
      <Navigate
        to={BOTHY_PATH}
        replace
        state={state.user ? { unauthorised: true } : undefined}
      />
    );
  }
  return typeof children === 'function' ? children(state.user) : children;
}
